import { useState, useEffect, useCallback } from 'react';

interface DOITimerConfig {
  registrationTimestamp: string | null;
  delayMinutes: number;
  enabled: boolean;
}

interface DOITimerReturn {
  /** Time remaining in seconds */
  timeRemaining: number;
  /** Whether the user is locked out (time has expired and not verified) */
  isLocked: boolean;
  /** Formatted time as "MM:SS" */
  formattedTime: string;
}

/**
 * Hook to manage DOI (Double Opt-In) verification timer.
 * Calculates remaining time until the user gets locked out if not verified.
 *
 * @param config - Configuration object with registration timestamp, delay, and enabled flag
 * @returns Timer state including remaining time, locked status, and formatted time
 */
export function useDOITimer(config: DOITimerConfig): DOITimerReturn {
  const { registrationTimestamp, delayMinutes, enabled } = config;

  const calculateTimeRemaining = useCallback((): number => {
    if (!enabled || !registrationTimestamp || delayMinutes <= 0) {
      return 0;
    }

    const registrationDate = new Date(registrationTimestamp);
    const deadlineMs = registrationDate.getTime() + delayMinutes * 60 * 1000;
    const nowMs = Date.now();
    const remainingMs = deadlineMs - nowMs;

    return Math.max(0, Math.floor(remainingMs / 1000));
  }, [registrationTimestamp, delayMinutes, enabled]);

  const [timeRemaining, setTimeRemaining] = useState<number>(() => calculateTimeRemaining());

  useEffect(() => {
    // Recalculate immediately when config changes
    setTimeRemaining(calculateTimeRemaining());

    // If not enabled or no delay, don't start the interval
    if (!enabled || delayMinutes <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [calculateTimeRemaining, enabled, delayMinutes]);

  // Calculate locked state
  const isLocked = enabled && delayMinutes > 0 && timeRemaining === 0;

  // Format time as MM:SS
  const formatTime = useCallback((seconds: number): string => {
    if (seconds <= 0) return '00:00';

    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const formattedTime = formatTime(timeRemaining);

  return {
    timeRemaining,
    isLocked,
    formattedTime,
  };
}

export type { DOITimerConfig, DOITimerReturn };
